if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        // The arrows, the arrow keys and a sideways swipe all step through the
        // images the gallery shows (navItems), stopping at either end.
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-modal-nav]');
          if (button) this.step(button.dataset.modalNav === 'next' ? 1 : -1);
          const thumb = event.target.closest('[data-modal-thumb]');
          if (thumb) {
            const index = Number(thumb.dataset.modalThumb);
            const items = this.navItems();
            this.goTo(index, index < this.activeIndex(items) ? -1 : 1);
          }
        });
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          // Video and 3D controls use the arrow keys themselves.
          if (event.target.closest('deferred-media, product-model, video, model-viewer')) return;
          event.preventDefault();
          this.step(event.key === 'ArrowRight' ? 1 : -1);
        });
        // ModalDialog closes a media modal on any mouse pointerup outside video
        // and 3D media, so a click on an arrow or a thumbnail, or just between
        // them, would close it.
        const dialog = this.querySelector('[role="dialog"]');
        if (dialog) {
          dialog.addEventListener('pointerup', (event) => {
            if (event.target.closest('.product-media-modal__nav-row')) event.stopPropagation();
          });
        }
        this.bindSwipe();
      }

      show(opener) {
        // The opener stays in openedBy, which ModalDialog returns focus to.
        this.activeMediaId = opener && opener.getAttribute('data-media-id');
        this.direction = 1;
        // Before super.show(): its focus trap counts the enabled buttons.
        this.updateNav();
        const status = this.querySelector('[data-modal-status]');
        if (status) status.textContent = '';
        super.show(opener);
        this.showActiveMedia();
      }

      // The items the gallery shows, in its order: only the shopper's arm of the
      // gallery test (TASKS.md #149), and of the pack-size shots only the first,
      // the selected one, which the challenger hides on phones until the
      // shopper changes quantity (data-gallery-landing). product-info.js
      // re-renders the list on a pack change.
      navItems() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return [];
        const challenger = document.documentElement.classList.contains('gallery-challenger');
        const carousel = !window.matchMedia('(min-width: 750px)').matches;
        // The challenger's per-device sets: the query in base-custom.css.
        const desktop = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches;
        // Beside the phone set no pack shot shows (its aspect ratio differs).
        const phoneSet = challenger && !desktop && !!content.querySelector('[data-gallery-device="mobile"]');
        return Array.from(content.children).filter((el) => {
          if (!el.hasAttribute('data-media-id')) return false;
          const arm = el.getAttribute('data-gallery-arm');
          if (phoneSet && !arm) return false;
          if (arm && (arm === 'challenger') !== challenger) return false;
          const device = el.getAttribute('data-gallery-device');
          if (device && (device === 'desktop') !== desktop) return false;
          if (challenger && carousel && el.hasAttribute('data-gallery-landing')) return false;
          return !el.classList.contains('product__media-item--variant') || el === content.firstElementChild;
        });
      }

      activeIndex(items) {
        return items.findIndex((el) => el.getAttribute('data-media-id') === this.activeMediaId);
      }

      step(direction) {
        this.goTo(this.activeIndex(this.navItems()) + direction, direction);
      }

      goTo(index, direction) {
        const items = this.navItems();
        const target = items[index];
        if (!target || index === this.activeIndex(items)) return;
        window.pauseAllMedia();
        this.activeMediaId = target.getAttribute('data-media-id');
        this.direction = direction;
        this.showActiveMedia();
        this.updateNav();
        const status = this.querySelector('[data-modal-status]');
        if (status && status.dataset.template) {
          status.textContent = status.dataset.template
            .replace('[index]', index + 1)
            .replace('[count]', items.length);
        }
      }

      updateNav() {
        const items = this.navItems();
        const index = this.activeIndex(items);
        const single = items.length < 2;
        // Nothing to step to: the row is hidden and its buttons disabled, so the
        // focus trap doesn't count a button nobody can reach as its last stop.
        const row = this.querySelector('.product-media-modal__nav-row');
        if (row) row.hidden = single;
        this.querySelectorAll('[data-modal-nav]').forEach((button) => {
          button.disabled = single;
          // At an end the arrow stays focusable: a focused button that becomes
          // disabled drops keyboard focus to <body>.
          const atEnd = button.dataset.modalNav === 'next' ? index >= items.length - 1 : index <= 0;
          button.setAttribute('aria-disabled', String(!single && atEnd));
        });
        this.updateThumbs(items, index, single);
      }

      // One button per image the arrows step through, rebuilt only when that
      // list changes (a pack change re-renders it, and the gallery test's arm
      // and the device decide what is in it), so a focused thumbnail survives
      // a step.
      updateThumbs(items, index, single) {
        const strip = this.querySelector('[data-modal-thumbs]');
        if (!strip) return;
        const key = single ? '' : items.map((el) => el.getAttribute('data-media-id')).join(' ');
        if (strip.dataset.key !== key) {
          strip.dataset.key = key;
          strip.replaceChildren();
          const template = strip.dataset.labelTemplate || '';
          items.forEach((item, i) => {
            // Dawn's thumbnail markup, so these look and behave like the
            // gallery's own thumbnails on the page.
            const cell = document.createElement('div');
            cell.className = 'thumbnail-list__item product-media-modal__thumb-item';
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'thumbnail product-media-modal__thumb';
            if (item.nodeName !== 'IMG') button.classList.add('product-media-modal__thumb--video');
            button.dataset.modalThumb = i;
            button.setAttribute('aria-label', template.replace('[index]', i + 1).replace('[count]', items.length));
            const src = item.getAttribute('data-thumb');
            if (src) {
              const image = document.createElement('img');
              image.src = src;
              image.alt = '';
              image.width = 64;
              image.height = 64;
              image.loading = 'lazy';
              image.draggable = false;
              button.appendChild(image);
            }
            cell.appendChild(button);
            strip.appendChild(cell);
          });
        }
        const hadFocus = strip.contains(document.activeElement);
        let current = null;
        strip.querySelectorAll('[data-modal-thumb]').forEach((button) => {
          const active = Number(button.dataset.modalThumb) === index;
          if (active) current = button;
          if (active) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        if (!current) return;
        // Keep the current thumbnail in view: scroll the strip only, never the page.
        const cell = current.parentElement;
        const left = cell.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
        if (left < strip.scrollLeft || left + cell.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollLeft = left - (strip.clientWidth - cell.offsetWidth) / 2;
        }
        if (hadFocus && document.activeElement !== current) current.focus({ preventScroll: true });
      }

      showActiveMedia() {
        let activeMedia =
          this.activeMediaId && this.querySelector(`[data-media-id="${this.activeMediaId}"]`);
        if (!activeMedia) {
          activeMedia = this.navItems()[0];
          if (!activeMedia) return;
          this.activeMediaId = activeMedia.getAttribute('data-media-id');
        }
        this.querySelectorAll('[data-media-id].active').forEach((element) => {
          if (element !== activeMedia) element.classList.remove('active');
        });
        activeMedia.classList.add('active');

        // The images are lazy, and a lazy image only starts loading at the
        // browser's next check after it is shown. Start this one now, and
        // the ones around it once it has arrived, since it needs the
        // bandwidth first.
        const image = this.imageOf(activeMedia);
        if (image) image.loading = 'eager';
        if (image && !image.complete) {
          const warm = () => this.warmNeighbours();
          image.addEventListener('load', warm, { once: true });
          image.addEventListener('error', warm, { once: true });
        } else {
          this.warmNeighbours();
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaTemplate &&
          activeMediaTemplate.content.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      // Load the next two images in the direction of travel and the one
      // behind, so the next press shows its image at once instead of waiting
      // on the network. A hidden lazy image never loads by itself; switching
      // it to eager fetches the same file it would pick when shown. One at a
      // time, nearest first, so on a slow connection the next image doesn't
      // share the bandwidth with the ones after it. Skipped once the lightbox
      // has closed, and when the shopper's browser asks to save data or is
      // on 2G.
      warmNeighbours() {
        if (!this.hasAttribute('open')) return;
        const connection = navigator.connection;
        if (connection && (connection.saveData || /2g$/.test(connection.effectiveType || ''))) return;
        const items = this.navItems();
        const index = this.activeIndex(items);
        if (index < 0) return;
        // A later press may have shown an image still loading; its own load
        // event warms around it.
        const shown = this.imageOf(items[index]);
        if (shown && !shown.complete) return;
        const ahead = this.direction < 0 ? -1 : 1;
        // The nearest image not yet loaded (or failed) after being started.
        const image = [index + ahead, index + 2 * ahead, index - ahead]
          .map((i) => this.imageOf(items[i]))
          .find((candidate) => candidate && !(candidate.loading === 'eager' && candidate.complete));
        if (!image) return;
        image.loading = 'eager';
        const warm = () => this.warmNeighbours();
        image.addEventListener('load', warm, { once: true });
        image.addEventListener('error', warm, { once: true });
      }

      // An image item is the <img> itself; a video or 3D item has a poster.
      imageOf(item) {
        if (!item) return null;
        return item.tagName === 'IMG' ? item : item.querySelector('img');
      }

      // The phone's own pinch-zoom; the lightbox has no zoom of its own.
      isZoomed() {
        return !!window.visualViewport && window.visualViewport.scale > 1.01;
      }

      bindSwipe() {
        const content = this.querySelector('[role="document"]');
        if (!content) return;
        let startX = null;
        let startY = 0;
        content.addEventListener(
          'touchstart',
          (event) => {
            // A second finger means a pinch, and one finger on a pinch-zoomed
            // page is panning around the image: neither is a swipe.
            if (event.touches.length > 1 || this.isZoomed()) {
              startX = null;
              return;
            }
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
          },
          { passive: true }
        );
        content.addEventListener(
          'touchend',
          (event) => {
            if (startX === null) return;
            const dx = event.changedTouches[0].clientX - startX;
            const dy = event.changedTouches[0].clientY - startY;
            startX = null;
            // Mostly vertical, or too short to be a swipe.
            if (Math.abs(dy) > Math.abs(dx) || Math.abs(dx) < 40) return;
            this.step(dx < 0 ? 1 : -1);
          },
          { passive: true }
        );
      }
    }
  );
}
